import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { cn } from '~/lib/cn';
import { useContent } from '~/lib/useLanguage';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared popup chrome (the app-download QR modal treatment): a blur backdrop
 * that closes on click, a card that rises from the bottom, and a close button
 * that follows the cursor while it is over the backdrop. Stays mounted while
 * closed (`inert`) so the exit transition can play. While open it also handles
 * Escape, locks body scroll, keeps Tab inside the card, and hands focus back to
 * the trigger on close. Callers render the card itself as `children`.
 */
export function ModalShell({
  open,
  onClose,
  label,
  labelledBy,
  initialFocusRef,
  className,
  children,
}: {
  open: boolean;
  onClose: () => void;
  /** Accessible name when the card has no visible heading to point at. */
  label?: string;
  /** Id of the card's visible heading. */
  labelledBy?: string;
  /** Element focused on open (e.g. the card's close button); the dialog itself otherwise. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Sizing for the dialog wrapper (e.g. a max width for wide cards). */
  className?: string;
  children: ReactNode;
}) {
  const { ui } = useContent();
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  const [overCard, setOverCard] = useState(false);
  const [tracking, setTracking] = useState(false);
  const rafRef = useRef<number | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Escape closes; Tab stays inside the card; lock body scroll; restore focus.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key !== 'Tab' || !dialog) return;

      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = focusable[0];
      const last = focusable.at(-1);
      const active = document.activeElement;
      if (!first || !last) {
        e.preventDefault();
      } else if (e.shiftKey && (active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    (initialFocusRef?.current ?? dialog)?.focus({ preventScroll: true });
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      // Hand focus back only if the caller hasn't moved it somewhere on purpose
      // (e.g. a CTA that closes the modal and focuses a form on the page).
      const active = document.activeElement;
      if (!active || active === document.body || dialog?.contains(active)) {
        previouslyFocused?.focus({ preventScroll: true });
      }
    };
  }, [open, onClose, initialFocusRef]);

  // Reset the follower whenever the modal is closed.
  useEffect(() => {
    if (!open) {
      setTracking(false);
      setOverCard(false);
    }
  }, [open]);

  const onPointerMove = (e: React.PointerEvent) => {
    const { clientX, clientY } = e;
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setCursor({ x: clientX, y: clientY });
      setTracking(true);
    });
  };

  const followerVisible = open && tracking && !overCard;

  return (
    <div
      inert={!open}
      onPointerMove={onPointerMove}
      className={cn(
        'fixed inset-0 z-[100] flex items-end justify-center px-5 pb-[12vh] sm:items-center sm:pb-0',
        open ? '' : 'pointer-events-none',
      )}>
      {/* Shared blur backdrop (same tint/blur as the mega menu). Blur applies
          instantly on open — no opacity fade — so it never "ramps up".
          Pointer cursor signals it's clickable (closes the modal). */}
      <button
        type="button"
        aria-label={ui.close}
        onClick={onClose}
        className={cn(
          'absolute inset-0 cursor-pointer bg-blur-backdrop/60 backdrop-blur-sm',
          open ? 'opacity-100' : 'opacity-0',
        )}
      />

      {/* Card rises up from the bottom. */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={label}
        aria-labelledby={labelledBy}
        onPointerEnter={() => setOverCard(true)}
        onPointerLeave={() => setOverCard(false)}
        className={cn(
          'relative outline-none transition-all duration-500 ease-nav',
          open ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0',
          className,
        )}>
        {children}
      </div>

      {/* Close button that follows the cursor over the backdrop. It's
          pointer-events-none so clicks pass through to the backdrop below. */}
      <div
        aria-hidden
        style={{ left: cursor.x, top: cursor.y }}
        className={cn(
          'pointer-events-none fixed z-[110] -translate-x-1/2 -translate-y-1/2 transition-opacity duration-150',
          followerVisible ? 'opacity-100' : 'opacity-0',
        )}>
        <div className="flex size-14 items-center justify-center rounded-full bg-white text-grey-900">
          <svg
            viewBox="0 0 24 24"
            className="size-5"
            stroke="currentColor"
            strokeWidth="2"
            fill="none">
            <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
          </svg>
        </div>
      </div>
    </div>
  );
}
